import type { GeoJsonFeatureCollection, MapFeatureMapStyle } from './map.js'
import type { LatLngBounds, ResolvedPlace } from './places.js'

/** Minimal GeoJSON geometry shapes for API `geo_json` on geofences. */
export type GeofenceGeometry =
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] }
  | { type: string; coordinates?: unknown }

/**
 * API keyword from gpx-engine `GeofenceResource`:
 * `custom` = arbitrary polygon; `radius` = circle (center + radius). Prefer this over `is_irregular` for behaviour.
 */
export type GeofenceApiTypeKeyword = 'custom' | 'radius'

/**
 * Account geofence as returned by the GPX API (`GeofenceResource` in gpx-engine).
 * Keep in sync with `App\Http\Resources\GeofenceResource`; optional fields mirror Laravel `when()` / conditional keys.
 */
export type Geofence = {
  id: number
  /** @deprecated Present for backward compatibility; prefer `account` relation when loaded. */
  account_id?: number
  name: string
  is_alertable?: boolean
  /** Still returned by API; prefer {@link Geofence.type} for shape logic. */
  is_irregular?: boolean
  type?: GeofenceApiTypeKeyword
  address?: string | null
  full_name?: string | null
  email?: string | null
  phone?: string | null
  company_name?: string | null
  allowable_dwell_time?: number | null
  allowable_dwell_days?: number | null
  max_assets?: number | null
  min_assets?: number | null
  notes?: string | null
  /** Center latitude; may be omitted when only `geo_json` is present. */
  lat?: number
  /** Center longitude; may be omitted when only `geo_json` is present. */
  lng?: number
  /** Radius in meters for `type === 'radius'`; may be omitted. */
  radius?: number
  /** Omitted for many radius geofences; always present for `type === 'custom'`. */
  geo_json?: GeofenceGeometry | null
  account?: unknown
  events?: unknown
  exits?: unknown
  enters?: unknown
  devices_count?: number
  not_in_geofence_devices_count?: number
  avg_time?: number | null
  average_dwell_time_percentage?: number | null
  assets_exceeding_dwell?: unknown
}

/**
 * `true` when the fence is an arbitrary polygon (`geo_json` required for display).
 * Uses {@link Geofence.type} first; falls back to `is_irregular` if `type` is missing.
 */
export function isGeofenceCustom(g: Geofence): boolean {
  if (g.type === 'custom') return true
  if (g.type === 'radius') return false
  return Boolean(g.is_irregular)
}

/** `type === 'radius'` or implied circle (not custom). */
export function isGeofenceRadius(g: Geofence): boolean {
  return !isGeofenceCustom(g)
}

/**
 * `properties` on GeoJSON features for **circle** geofences on GPX maps (native `L.circle` / Mapbox circle layer).
 * Do not duplicate fence fields here — resolve via {@link Geofence.id} if needed.
 */
export type GeofenceMapCircleProperties = {
  type: 'circle'
  center: [number, number]
  radius: number
  geofenceId: number
  map?: MapFeatureMapStyle
}

export type GeofencesToGeoJsonOptions = {
  map?: MapFeatureMapStyle
  mapFor?: (geofence: Geofence, index: number) => MapFeatureMapStyle | undefined
  idPrefix?: string
}

function isPolygonLike(g: GeofenceGeometry | null | undefined): g is GeofenceGeometry & { type: 'Polygon' | 'MultiPolygon' } {
  return g != null && (g.type === 'Polygon' || g.type === 'MultiPolygon')
}

function optionalFiniteNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v !== '' && Number.isFinite(Number(v))) return Number(v)
  return undefined
}

function optionalStringOrNull(v: unknown): string | null | undefined {
  if (v === undefined) return undefined
  if (v === null) return null
  return String(v)
}

function deriveLatLngFromGeoJson(
  custom: boolean,
  geojson: GeofenceGeometry | null | undefined
): { lat: number; lng: number } | null {
  if (!custom || !geojson) return null
  if (geojson.type === 'Polygon') {
    const coords = geojson.coordinates as number[][][] | undefined
    const first = coords?.[0]?.[0]
    if (first && first.length >= 2) return { lat: first[1]!, lng: first[0]! }
  }
  if (geojson.type === 'MultiPolygon') {
    const coords = geojson.coordinates as number[][][][] | undefined
    const first = coords?.[0]?.[0]?.[0]
    if (first && first.length >= 2) return { lat: first[1]!, lng: first[0]! }
  }
  return null
}

/**
 * Center (lat/lng) for routing and labels: API `lat`/`lng`, or first vertex of polygon when missing (custom only).
 */
export function geofenceCenterLatLng(g: Geofence): { lat: number; lng: number } | null {
  const lat = optionalFiniteNumber(g.lat)
  const lng = optionalFiniteNumber(g.lng)
  if (lat !== undefined && lng !== undefined) return { lat, lng }
  return deriveLatLngFromGeoJson(isGeofenceCustom(g), g.geo_json ?? null)
}

export function geofenceRadiusMeters(g: Geofence): number {
  const r = optionalFiniteNumber(g.radius)
  return r !== undefined && Number.isFinite(r) ? r : 0
}

/**
 * Maps a geofence to {@link ResolvedPlace} for Places UI, routing waypoints, and labels.
 * Prefer `address` for display; fallback `name`. Stable synthetic `providerPlaceId` for identity.
 */
export function geofenceToResolvedPlace(g: Geofence): ResolvedPlace | null {
  const c = geofenceCenterLatLng(g)
  if (!c) return null
  const formatted =
    (typeof g.address === 'string' && g.address.trim()) ||
    (typeof g.name === 'string' && g.name.trim()) ||
    'Geofence'
  return {
    coordinates: c,
    formattedAddress: formatted,
    providerPlaceId: `gpx:geofence:${g.id}`,
  }
}

function circleRingLatLng(lat: number, lng: number, radiusM: number, segments: number): Array<{ lat: number; lng: number }> {
  const mPerDegLat = 111_320
  const cosLat = Math.cos((lat * Math.PI) / 180)
  const out: Array<{ lat: number; lng: number }> = []
  for (let i = 0; i <= segments; i++) {
    const t = (i / segments) * 2 * Math.PI
    const dy = (radiusM * Math.cos(t)) / mPerDegLat
    const dx = (radiusM * Math.sin(t)) / (mPerDegLat * Math.max(1e-6, Math.abs(cosLat)))
    out.push({ lat: lat + dy, lng: lng + dx })
  }
  return out
}

/** Lat/lng samples along the geofence outline for map fitBounds / focus. */
export function geofenceOutlineLatLngs(g: Geofence): Array<{ lat: number; lng: number }> {
  const center = geofenceCenterLatLng(g)
  if (isGeofenceCustom(g) && isPolygonLike(g.geo_json)) {
    if (g.geo_json.type === 'Polygon') {
      const ring = (g.geo_json.coordinates as number[][][])[0]
      if (!ring?.length) {
        return center ? [center] : []
      }
      return ring.map((pair) => {
        const [lng, lat] = pair as [number, number]
        return { lat, lng }
      })
    }
    const ring = (g.geo_json.coordinates as number[][][][])[0]?.[0]
    if (!ring?.length) {
      return center ? [center] : []
    }
    return ring.map((pair) => {
      const [lng, lat] = pair as [number, number]
      return { lat, lng }
    })
  }
  if (isGeofenceRadius(g) && isPolygonLike(g.geo_json) && g.geo_json.type === 'Polygon') {
    const ring = (g.geo_json.coordinates as number[][][])[0]
    if (ring?.length) {
      return ring.map((pair) => {
        const [lng, lat] = pair as [number, number]
        return { lat, lng }
      })
    }
  }
  const r = geofenceRadiusMeters(g)
  if (center && r > 0) {
    return circleRingLatLng(center.lat, center.lng, r, 32)
  }
  return center ? [center] : []
}

export function geofenceBounds(g: Geofence): LatLngBounds | null {
  const pts = geofenceOutlineLatLngs(g)
  if (pts.length === 0) return null
  let minLat = Infinity
  let maxLat = -Infinity
  let minLng = Infinity
  let maxLng = -Infinity
  for (const p of pts) {
    minLat = Math.min(minLat, p.lat)
    maxLat = Math.max(maxLat, p.lat)
    minLng = Math.min(minLng, p.lng)
    maxLng = Math.max(maxLng, p.lng)
  }
  return {
    southWest: { lat: minLat, lng: minLng },
    northEast: { lat: maxLat, lng: maxLng },
  }
}

/**
 * Normalizes one geofence from list/detail JSON (e.g. single-resource fetch). For large lists prefer raw API rows + helpers.
 */
export function normalizeGeofenceFromApi(raw: unknown): Geofence | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const id = r.id
  const name = r.name
  if (typeof id !== 'number' || typeof name !== 'string') return null

  const is_irregular = Boolean(r.is_irregular)
  const gjRaw = r.geo_json
  const geo_json =
    gjRaw && typeof gjRaw === 'object' && gjRaw !== null ? (gjRaw as GeofenceGeometry) : undefined

  let lat = optionalFiniteNumber(r.lat)
  let lng = optionalFiniteNumber(r.lng)
  const typeFromApi = r.type
  const apiType: GeofenceApiTypeKeyword | undefined =
    typeFromApi === 'custom' || typeFromApi === 'radius' ? typeFromApi : is_irregular ? 'custom' : 'radius'

  const customShape = apiType === 'custom' || (apiType !== 'radius' && is_irregular)

  if (lat === undefined || lng === undefined) {
    const d = deriveLatLngFromGeoJson(customShape, geo_json ?? null)
    if (d) {
      lat = d.lat
      lng = d.lng
    }
  }

  return {
    id,
    name,
    is_irregular,
    account_id: optionalFiniteNumber(r.account_id),
    is_alertable: typeof r.is_alertable === 'boolean' ? r.is_alertable : undefined,
    type: apiType,
    address: optionalStringOrNull(r.address) ?? null,
    full_name: optionalStringOrNull(r.full_name) ?? null,
    email: optionalStringOrNull(r.email) ?? null,
    phone: optionalStringOrNull(r.phone) ?? null,
    company_name: optionalStringOrNull(r.company_name) ?? null,
    allowable_dwell_time: optionalFiniteNumber(r.allowable_dwell_time) ?? null,
    allowable_dwell_days: optionalFiniteNumber(r.allowable_dwell_days) ?? null,
    max_assets: optionalFiniteNumber(r.max_assets) ?? null,
    min_assets: optionalFiniteNumber(r.min_assets) ?? null,
    notes: optionalStringOrNull(r.notes) ?? null,
    lat,
    lng,
    radius: optionalFiniteNumber(r.radius),
    geo_json,
    account: r.account,
    events: r.events,
    exits: r.exits,
    enters: r.enters,
    devices_count: optionalFiniteNumber(r.devices_count),
    not_in_geofence_devices_count: optionalFiniteNumber(r.not_in_geofence_devices_count),
    avg_time: optionalFiniteNumber(r.avg_time) ?? null,
    average_dwell_time_percentage: optionalFiniteNumber(r.average_dwell_time_percentage) ?? null,
    assets_exceeding_dwell: r.assets_exceeding_dwell,
  }
}

export function geofenceToFeature(
  g: Geofence,
  options?: { map?: MapFeatureMapStyle; featureId?: string }
): GeoJsonFeatureCollection['features'][0] & { id?: string } | null {
  const map = options?.map
  const featureId = options?.featureId ?? `geofence-${g.id}`
  const mapStyle = map && Object.keys(map).length > 0 ? map : undefined

  const irregularProps: Record<string, unknown> = { geofenceId: g.id }
  if (mapStyle) irregularProps.map = mapStyle

  if (isGeofenceCustom(g)) {
    if (!isPolygonLike(g.geo_json)) return null
    return {
      type: 'Feature',
      id: featureId,
      properties: irregularProps,
      geometry: g.geo_json as GeoJsonFeatureCollection['features'][0]['geometry'],
    }
  }

  const center = geofenceCenterLatLng(g)
  if (!center) return null

  const radiusM = geofenceRadiusMeters(g)
  const circleProps: Record<string, unknown> = {
    type: 'circle',
    center: [center.lng, center.lat] as [number, number],
    radius: radiusM,
    geofenceId: g.id,
  }
  if (mapStyle) circleProps.map = mapStyle

  if (isPolygonLike(g.geo_json) && g.geo_json.type === 'Polygon') {
    return {
      type: 'Feature',
      id: featureId,
      properties: circleProps,
      geometry: g.geo_json as GeoJsonFeatureCollection['features'][0]['geometry'],
    }
  }

  return {
    type: 'Feature',
    id: featureId,
    properties: circleProps,
    geometry: {
      type: 'Point',
      coordinates: [center.lng, center.lat],
    },
  }
}

export function geofencesToGeoJSON(geofences: Geofence[], options?: GeofencesToGeoJsonOptions): GeoJsonFeatureCollection {
  const prefix = options?.idPrefix ?? 'geofence'
  const features: GeoJsonFeatureCollection['features'] = []
  for (let i = 0; i < geofences.length; i++) {
    const g = geofences[i]!
    const map = options?.mapFor?.(g, i) ?? options?.map
    const f = geofenceToFeature(g, {
      map,
      featureId: `${prefix}-${g.id}-${i}`,
    })
    if (f) features.push(f)
  }
  return { type: 'FeatureCollection', features }
}
